import AddIcon from "@mui/icons-material/Add";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Grid, Typography } from "@mui/material";
import { FormikProps, useFormik } from "formik";
import { useEffect, useState } from "react";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useApiFeedback } from "../../../app/useApiFeedback";
import ApplicantDepandents from "../../../common/dropdowns/ApplicantDepandents";
import ApplicantGuardians from "../../../common/dropdowns/ApplicantGuardians";
import DocumentTypes from "../../../common/dropdowns/DocumentTypes";
import DocumentOwner from "../../../common/dropdowns/documentOwner";
import MessageResponse from "../../../common/feedback/messageResponse";
import {
  CreateDocumentDto,
  CreateDocumentOwner,
  InitialCreateDocumentOwnerState,
} from "../models";
import { selectAdmin } from "../adminSlice";
import { CreateDocumentAPIThunk } from "../thunks";

interface Props {
  applicantId: number;
}
export default function AddAdditionalDocuments({ applicantId }: Props) {
  const [activeTab, setActiveTab] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const admin = useAppSelector(selectAdmin);
  const { feedback, handleApiResponse } = useApiFeedback();

  const validationSchema = yup.object({
    dependantId: yup.number().when("documentOwnerId", {
      is: 1,
      then: (schema) =>
        schema
          .required("Please select dependant")
          .min(1, "Please select dependant"),
    }),
    guardianId: yup.number().when("documentOwnerId", {
      is: 2,
      then: (schema) =>
        schema
          .required("Please select guardian")
          .min(1, "Please select guardian"),
    }),
    documentOwnerId: yup
      .number()
      .required("Please select document owner")
      .min(0, "Please select document owner"),
    documentTypeId: yup
      .number()
      .required("Please select document type")
      .min(0, "Please select document type"),
  });

  const getDocumentOwner = (owner: number) => {
    if (owner === 1) return formik.values.dependantId;
    if (owner === 2) return formik.values.guardianId;

    return applicantId;
  };
  const formik: FormikProps<InitialCreateDocumentOwnerState> =
    useFormik<InitialCreateDocumentOwnerState>({
      initialValues: {
        dependantId: -1,
        documentOwnerId: -1,
        documentTypeId: -1,
        guardianId: -1,
      },
      validationSchema: validationSchema,
      onSubmit: (values: InitialCreateDocumentOwnerState) => {
        const payload = {
          applicantId: applicantId,
          documents: [
            {
              documentOwner: values.documentOwnerId,
              documentOwnerId: getDocumentOwner(values.documentOwnerId), //applicant or guardian or dependant,
              documentTypeId: values.documentTypeId,
            } as CreateDocumentOwner,
          ],
        } as CreateDocumentDto;

        dispatch(CreateDocumentAPIThunk(payload)).then((resp: any) => {
          handleApiResponse(resp, true);
        });
      },
    });

  useEffect(() => {
    if (feedback) {
      if (feedback.isSuccess) {
        formik.resetForm();
        setActiveTab(false);
      }
    }
  }, [feedback]);
  return (
    <>
      <MessageResponse feedback={feedback} />

      {!activeTab && (
        <Box
          className="AdditionalDocuments_Container"
          onClick={() => setActiveTab(true)}
        >
          <Box className="AdditionalDocuments_Container_Inner">
            <AddIcon className="AdditionalDocuments_icon" fontSize="small" />
            <Box className="AdditionalDocuments_text">Additional Documents</Box>
          </Box>
        </Box>
      )}

      {activeTab && (
        <Grid container spacing={1} mt={2}>
          <Grid item xs={6}>
            <DocumentOwner formik={formik} />
          </Grid>
          <Grid item xs={6}>
            <DocumentTypes formik={formik} />
          </Grid>

          {formik.values.documentOwnerId === 1 && (
            <Grid item xs={6}>
              <ApplicantDepandents formik={formik} />
            </Grid>
          )}
          {formik.values.documentOwnerId === 2 && (
            <Grid item xs={6}>
              <ApplicantGuardians formik={formik} />
            </Grid>
          )}

          <Grid item xs={12}>
            <LoadingButton
              sx={{ float: "left", marginBottom: 1.5 }}
              color="primary"
              variant="contained"
              startIcon={<SaveOutlinedIcon />}
              onClick={() => {
                formik.submitForm();
              }}
              disabled={!formik.dirty}
              loading={admin.applicantDocumentState.status === "loading"}
              loadingPosition="start"
            >
              Create Document & Update Status
            </LoadingButton>

            <Button variant="text" onClick={() => setActiveTab(false)}>
              Cancel
            </Button>
          </Grid>

          <Grid item xs={12} mb={3}>
            <Typography variant="body2" component="body" sx={{ color: "gray" }}>
              * Please note that this will automatically set the status to
              Documents Upload, you do not have to manually update the status.
            </Typography>
          </Grid>
        </Grid>
      )}
    </>
  );
}
